<template>
  <div>
    <h2 class="text-2xl font-bold mb-4">Contas</h2>
    <ConfirmDialog :draggable="false" />
    <DataTable :value="accounts" stripedRows paginator :rows="10">
      <Column field="name" header="Nome" sortable />
      <Column field="cnpj" header="CNPJ" sortable>
        <template #body="{ data }">
          {{ formatTaxId(data.cnpj) }}
        </template>
      </Column>
      <Column field="platform_country_code" header="Plataforma" sortable>
        <template #body="{ data }">
          <!-- Final once set: only accounts without a platform (e.g. created by SSO) get the picker. -->
          <span v-if="data.platform_country_code" class="flex items-center gap-2">
            <span :class="platformCountryFlagClass(data.platform_country_code)" class="shrink-0 rounded-sm" />
            {{ PLATFORM_COUNTRIES[data.platform_country_code as PlatformCountry].name }}
          </span>
          <PlatformCountrySelect
            v-else
            :model-value="pendingPlatformCountries[data.id] ?? null"
            placeholder="Não definida"
            size="small"
            class="min-w-36"
            @update:model-value="$emit('set-platform-country', data, $event)"
          />
        </template>
      </Column>
      <Column header="Reguladores">
        <template #body="{ data }">
          <div class="flex items-center gap-1.5">
            <div v-if="data.regulators?.length" class="flex flex-wrap gap-1">
              <span
                v-for="reg in data.regulators"
                :key="reg.slug"
                class="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600"
                :title="reg.name"
              >
                <BaseIcon size="xs" class="text-slate-400">{{ regulatorIcon(reg.slug) }}</BaseIcon>
                {{ reg.slug.toUpperCase() }}
              </span>
            </div>
            <span v-else class="text-xs text-slate-300">Nenhum</span>
            <Button
              class="p-button-rounded p-button-text p-button-sm"
              v-tooltip.top="'Editar reguladores'"
              @click="$emit('edit-regulators', data)"
            >
              <template #icon>
                <BaseIcon size="sm">edit</BaseIcon>
              </template>
            </Button>
          </div>
        </template>
      </Column>
      <Column field="inserted_at" header="Criado em" sortable>
        <template #body="{ data }">
	          {{ formatDate(data.inserted_at) }}
        </template>
      </Column>
      <Column header="Ações">
        <template #body="{ data }">
          <Button
            class="p-button-rounded p-button-info p-button-text mr-2"
            @click="$emit('configure-workflow', data)"
            v-tooltip.top="'Configurar Workflows'"
          >
            <template #icon>
              <span class="material-icons">settings_suggest</span>
            </template>
          </Button>
          <Button
            class="p-button-rounded p-button-secondary p-button-text mr-2"
            @click="$emit('manage-kyx-pricing', data)"
            v-tooltip.top="'Preços KYX'"
          >
            <template #icon>
              <BaseIcon>toll</BaseIcon>
            </template>
          </Button>
          <Button
            class="p-button-rounded p-button-warning p-button-text mr-2"
            @click="$emit('manage-billing', data)"
            v-tooltip.top="'Gestão Financeira (Planos/Tokens)'"
          >
            <template #icon>
              <span class="material-icons">payments</span>
            </template>
          </Button>
          <Button class="p-button-rounded p-button-danger p-button-text" @click="confirmDeleteAccount(data.id)">
            <template #icon>
              <span class="material-icons">delete</span>
            </template>
          </Button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import type { Account } from '@/types/account';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import { formatDate, formatTaxId } from '@/utils/formatters';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { regulatorIcon } from '@/constants/regulatorIcons';
import PlatformCountrySelect from '@/components/common/PlatformCountrySelect.vue';
import { PLATFORM_COUNTRIES, platformCountryFlagClass, type PlatformCountry } from '@/platformCountry';

defineProps<{
  accounts: Account[];
  // Platform picked in a row but not saved yet (awaiting confirmation or the request).
  pendingPlatformCountries: Record<string, PlatformCountry>;
}>();

const emit = defineEmits([
  'delete',
  'configure-workflow',
  'edit-regulators',
  'set-platform-country',
  'manage-billing',
  'manage-kyx-pricing',
]);

const { confirmDelete } = useConfirmDialog();

const confirmDeleteAccount = (accountId: string) => {
  confirmDelete({
    message: 'Tem certeza que deseja excluir esta conta?',
    header: 'Confirmar Exclusão',
    accept: () => {
      emit('delete', accountId);
    },
  });
};
</script>
